import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const NAV = [
  { label: "Protocol", path: "/protocol" },
  { label: "Orchestra", path: "/orchestra" },
  { label: "Archive", path: "/archive" },
];

export const grantAccess = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("triarch:request-access"));
  }
};

export const TriarchHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const isActive = (p) => location.pathname === p;

  return (
    <header
      data-testid="site-header"
      className={
        "fixed inset-x-0 top-0 z-50 transition-colors duration-500 " +
        (scrolled
          ? "border-b border-white/10 bg-[#07080A]/70 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent")
      }
    >
      <div className="mx-auto flex h-20 max-w-6xl items-center justify-between px-5 sm:px-8">
        <Link
          to="/"
          data-testid="site-header-logo"
          className="group flex items-center gap-3"
        >
          <img
            src="/triarch-logo-gold.png"
            alt="Triarch"
            className="h-12 w-12 opacity-90 transition-opacity duration-300 group-hover:opacity-100 sm:h-14 sm:w-14"
          />
          <span className="font-display text-base font-medium tracking-[0.34em] text-white/90">
            TRIARCH
          </span>
        </Link>

        <nav
          data-testid="site-header-nav"
          className="hidden items-center gap-9 md:flex"
        >
          {NAV.map((n) => (
            <Link
              key={n.path}
              to={n.path}
              data-testid={"site-header-nav-" + n.label.toLowerCase()}
              className={
                "relative font-mono-accent text-xs uppercase tracking-[0.22em] transition-colors duration-300 " +
                (isActive(n.path)
                  ? "text-white"
                  : "text-white/45 hover:text-white/90")
              }
            >
              {n.label}
              <span
                className={
                  "absolute -bottom-2 left-0 h-px bg-white/60 transition-all duration-300 " +
                  (isActive(n.path) ? "w-full" : "w-0")
                }
              />
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2.5">
          <button
            data-testid="site-header-request-access-button"
            onClick={grantAccess}
            className="group hidden items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-5 py-2 font-mono-accent text-xs uppercase tracking-[0.18em] text-white/80 transition-all duration-300 hover:border-[#C9AE78]/40 hover:bg-white/[0.08] hover:text-white hover:shadow-[0_0_28px_rgba(203,174,120,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 sm:inline-flex"
          >
            Request Access
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="site-header-mobile-toggle"
            aria-label="Toggle menu"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/80 transition-colors hover:text-white md:hidden"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/10 bg-[#07080A]/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col px-5 py-3">
              {NAV.map((n) => (
                <Link
                  key={n.path}
                  to={n.path}
                  className="border-b border-white/[0.06] py-3 font-mono-accent text-xs uppercase tracking-[0.22em] text-white/60 transition-colors hover:text-white"
                >
                  {n.label}
                </Link>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  grantAccess();
                }}
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-2.5 font-mono-accent text-xs uppercase tracking-[0.18em] text-[#07080A]"
              >
                Request Access <ArrowUpRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
